import { differenceGroupBySchema } from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  DateRangePicker,
  SkeletonRows,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  AttendantsTab,
  DayEndReportTab,
  DifferencesTab,
  ZReconciliationTab,
} from "../../../features/shifts/report-tabs.js";
import { useShiftPermissions } from "../../../features/shifts/queries.js";
import type { ReportSort } from "../../../features/shifts/report-values.js";
import {
  NoStations,
  ShiftStationField,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../features/shifts/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const REPORT_TABS = ["attendants", "differences", "z-reconciliation", "day-end"] as const;
type ReportTab = (typeof REPORT_TABS)[number];

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...shiftsSearchShape,
  tab: z.enum(REPORT_TABS).optional().catch(undefined),
  from: localDateText,
  to: localDateText,
  diffGroup: differenceGroupBySchema.optional().catch(undefined),
});

export const Route = createFileRoute("/_app/vardiya/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ShiftReportsPage,
});

/**
 * Vardiya raporları: pompacı performansı, açık / fazla trendi, Z mutabakatı ve gün sonu (zincir toplamlı).
 * Sekme URL'de taşınır (`?tab=`); ortak süzgeçler (şirket, istasyon, iş günü aralığı) sekmeler arasında korunur.
 */
function ShiftReportsPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  return (
    <ShiftsPage
      title={t("menu.shiftReports")}
      description={t("shifts.reports.description")}
      breadcrumb={[{ label: t("menu.shiftReports") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <ShiftReportsBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -29), to: today };
}

function ShiftReportsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const shape = useTenantShape();
  const station = useShiftStation(companyId);
  const [sort, setSort] = useState<ReportSort | null>(null);
  const tab: ReportTab = search.tab ?? "attendants";
  const fallback = defaultRange();
  const range = {
    from: search.from ?? fallback.from,
    to: search.to ?? fallback.to,
    stationId: station.stationId ?? undefined,
  };

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  const props = { companyId, range, showStationColumn: !shape.isSingleStation };

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <ShiftStationField selection={station} />
        <DateRangePicker
          label={t("shifts.reports.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) => setSearch({ from: next.from ?? undefined, to: next.to ?? undefined })}
        />
      </Card>

      <Tabs className="grid gap-4" value={tab} onValueChange={(next) => setSearch({ tab: next })}>
        <TabsList aria-label={t("menu.shiftReports")} className="flex-wrap">
          {REPORT_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`shifts.reports.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* İçerik MUTLAKA `TabsContent` içinde: tetikleyicinin `aria-controls` hedefi var olmalı. */}
        <TabsContent value={tab} className="grid gap-4 pt-0">
          {tab === "attendants" ? <AttendantsTab {...props} sort={sort} onSortChange={setSort} /> : null}
          {tab === "differences" ? (
            <DifferencesTab
              {...props}
              groupBy={search.diffGroup ?? "day"}
              onGroupByChange={(diffGroup) => setSearch({ diffGroup })}
            />
          ) : null}
          {tab === "z-reconciliation" ? <ZReconciliationTab {...props} /> : null}
          {tab === "day-end" ? <DayEndReportTab {...props} /> : null}
        </TabsContent>
      </Tabs>
    </>
  );
}
